import { Controller, useFormContext } from 'react-hook-form';
import { TextFieldProps as TextfieldPropsType } from '@mui/material/TextField';
import { DatePicker, DatePickerProps as DatePickerPropsType } from '@mui/x-date-pickers';
import dayjs, { Dayjs } from 'dayjs';

type Props = {
  DatePickerProps?: Partial<DatePickerPropsType<Dayjs>>;
  TextFieldProps?: TextfieldPropsType;
  defaultValue?: Date | undefined;
  name: string;
};

const HookFormDatePicker = ({
  name,
  defaultValue,
  DatePickerProps = {},
  TextFieldProps = {},
}: Props) => {
  const {
    formState: { errors },
  } = useFormContext();

  const error = errors[name];
  const displayError = Boolean(error?.message);

  // Convert default value to dayjs, but keep it as null if not provided
  const dayjsDefaultValue = defaultValue ? dayjs(defaultValue) : null;

  return (
    <>
      <Controller
        name={name}
        defaultValue={dayjsDefaultValue}
        render={({ field: { onChange, value } }) => {
          // Ensure value is always a dayjs object or null
          const dayjsValue = value ? (dayjs.isDayjs(value) ? value : dayjs(value)) : null;

          // Handle onChange to maintain dayjs format in form
          const handleChange = (newValue: Dayjs | null) => {
            if (newValue && typeof newValue === 'object' && typeof newValue.toDate === 'function')
              onChange(newValue.toDate());
            else onChange(newValue);
          };

          return (
            <DatePicker
              {...DatePickerProps}
              onChange={handleChange}
              value={dayjsValue}
              orientation="landscape"
              slotProps={{
                // Forward the caller's TextFieldProps. Previously only
                // helperText made it through, so `size`, `sx` and every other
                // field prop were accepted and then silently dropped.
                textField: {
                  ...TextFieldProps,
                  error: displayError,
                },
              }}
            />
          );
        }}
      />
    </>
  );
};

export default HookFormDatePicker;
